/**
 * Hilfsfunktionen für Deep-Links auf Detailansichten (`/events/42`, `/accounts/7`, …)
 * und für das Merken des Ziels über den Login hinweg.
 *
 * Bewusst router-frei und rein, damit sie ohne Router-Kontext testbar sind
 * (gleiches Muster wie `moduleEnabled.ts`).
 */

/** Pfade, die als Login-Ziel sinnlos oder gefährlich (Schleife) wären. */
const NON_TARGET_PATHS = new Set(['/', '/login', '/dashboard']);

/**
 * Liest die Datensatz-ID aus einem Routenparameter.
 *
 * Absichtlich strenger als `Number()`: Das Backend vergibt ausschließlich positive
 * Ganzzahlen, und `Number()` würde ` 42 `, `0x2a`, `1e3` oder `Infinity` klaglos
 * akzeptieren — daraus entstünden Anfragen auf IDs, die so nie in einer URL stehen.
 */
export function parseDetailId(rawId: string | undefined): number | null {
    if (!rawId || !/^[0-9]+$/.test(rawId)) {
        return null;
    }
    const parsed = Number(rawId);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Baut den Wert für `?next=` aus dem aktuellen Ziel. Query-Parameter bleiben
 * erhalten, damit z. B. `/myreservations/7?include_deleted=true` den Login übersteht.
 * Liefert `null`, wenn sich das Merken nicht lohnt oder eine Schleife entstünde.
 */
export function buildNextParam(pathname: string, search: string): string | null {
    if (NON_TARGET_PATHS.has(pathname)) {
        return null;
    }
    return `${pathname}${search}`;
}

/** Steuerzeichen-Pruefung ohne Regex-Literal (ESLint `no-control-regex`). */
function containsControlCharacter(value: string): boolean {
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index);
        if (code <= 0x1f || code === 0x7f) {
            return true;
        }
    }
    return false;
}

/**
 * Prüft einen `next`-Wert, bevor nach ihm navigiert wird.
 *
 * Erlaubt ist ausschließlich ein interner Pfad: genau ein führender `/`, gefolgt
 * von etwas, das kein zweiter Slash oder Backslash ist. Damit fallen sowohl
 * absolute URLs (`https://…`) als auch protokollrelative (`//host`) und die
 * Backslash-Varianten weg, die Browser beim Parsen wie `/` behandeln.
 */
export function sanitizeNextPath(rawNext: string | null | undefined): string | null {
    if (!rawNext) {
        return null;
    }

    // Steuerzeichen (Tab/Newline/CR) werden von Browsern beim URL-Parsen einfach
    // entfernt: `/<TAB>/evil.example` landet real als `//evil.example`. Statt die
    // Eingabe still umzuschreiben, wird sie abgelehnt — ein legitimer interner
    // Pfad enthält nie Steuerzeichen.
    if (containsControlCharacter(rawNext)) {
        return null;
    }

    const candidate = rawNext.trim();
    if (!candidate.startsWith('/') || candidate.startsWith('//') || candidate.startsWith('/\\')) {
        return null;
    }

    const withoutHash = candidate.split('#')[0];
    const pathname = withoutHash.split('?')[0];
    if (NON_TARGET_PATHS.has(pathname)) {
        return null;
    }

    return withoutHash;
}

/** Ziel-URL für den Login-Redirect, inkl. gemerktem `next`. */
export function buildLoginRedirect(pathname: string, search: string): string {
    const next = buildNextParam(pathname, search);
    return next ? `/login?next=${encodeURIComponent(next)}` : '/login';
}
